import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import * as graphql from '@treams/treams-network';

import MigrationSection, {
	type DryRunRecord,
	type MigrationRecord,
	useMigrationMatching,
} from './MigrationSection';

export default function EmployeesSection() {
	const { t } = useTranslation();
	const employeesQuery = graphql.useMigrationEmployeesQuery({
		variables: { status: graphql.UserStatus.Active, first: 10000 },
	});
	const dryRunQuery = graphql.useConnectorDryRunUsersQuery({
		variables: { first: 10000 },
	});
	const [updateUser] = graphql.useUpdateUserMutation();

	const sortByName = (a: { lastName: string; firstName: string }, b: typeof a) =>
		a.lastName.localeCompare(b.lastName) || a.firstName.localeCompare(b.firstName);

	const existing = useMemo(
		(): MigrationRecord[] =>
			[...(employeesQuery.data?.users.edges ?? [])]
				.sort((a, b) => sortByName(a.node, b.node))
				.map(({ node }) => ({
					id: node.id,
					externalReferenceID: node.externalReferenceID,
					matchKey: node.email,
					name: `${node.firstName} ${node.lastName}`,
					subtitle: node.email,
				})),
		[employeesQuery.data],
	);

	const dryRun = useMemo(
		(): DryRunRecord[] =>
			[...(dryRunQuery.data?.viewer.workspace.connector?.dryRun.users.edges ?? [])]
				.sort((a, b) => sortByName(a.node, b.node))
				.map(({ node, cursor }) => ({
					key: cursor,
					externalReferenceID: node.externalReferenceID,
					matchKey: node.email ?? '',
					name: `${node.firstName} ${node.lastName}`,
					subtitle: node.email ?? undefined,
				})),
		[dryRunQuery.data],
	);

	const { matched, unmatched, newItems } = useMigrationMatching(existing, dryRun);

	const employeeMap = useMemo(
		() => new Map((employeesQuery.data?.users.edges ?? []).map(({ node }) => [node.id, node])),
		[employeesQuery.data],
	);

	const handleSave = async (id: string, externalReferenceID: string) => {
		const employee = employeeMap.get(id);
		if (!employee || !employee.group?.id) throw new Error('Employee or department not found');
		await updateUser({
			variables: {
				input: {
					id: employee.id,
					firstName: employee.firstName,
					lastName: employee.lastName,
					email: employee.email,
					username: employee.username,
					locale: employee.locale,
					groupID: employee.group.id,
					externalReferenceID,
				},
			},
		});
	};

	return (
		<MigrationSection
			title={t('connectors.settings.migration.employees.title')}
			loading={employeesQuery.loading || dryRunQuery.loading}
			matched={matched}
			newItems={newItems}
			unmatched={unmatched}
			onSave={handleSave}
			onRefetch={() => employeesQuery.refetch()}
			emptyMessage={t('connectors.settings.migration.employees.empty')}
		/>
	);
}
